import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cargoApi } from '@/api/cargo';
import { Drawer, SectionCard, Field, StatusBadge } from '@/shared/ui/kit';
import { useToast } from '@/app/providers/ToastProvider';
import { fmt, dt } from '@/shared/format';
import {
  TRANSPORT_MODE,
  DELIVERY_METHOD,
  SHIPMENT_STATUS_COLOR,
  SCAN_ACTIONS,
  toOptions,
} from '@/shared/cargo';

interface FormState {
  FKCargoCustomerId: string;
  WarehouseCode: string;
  FKOriginWarehouseId: string;
  TransportMode: number;
  WeightKg: number;
  VolumeM3: number;
  Length: number;
  Width: number;
  Height: number;
  PieceCount: number;
  DeclaredContent: string;
  DeliveryMethod: number;
  AutoCharge: boolean;
  Charge: number;
  Note: string;
}

const empty: FormState = {
  FKCargoCustomerId: '',
  WarehouseCode: '',
  FKOriginWarehouseId: '',
  TransportMode: 0,
  WeightKg: 0,
  VolumeM3: 0,
  Length: 0,
  Width: 0,
  Height: 0,
  PieceCount: 1,
  DeclaredContent: '',
  DeliveryMethod: 0,
  AutoCharge: true,
  Charge: 0,
  Note: '',
};

export function ShipmentDrawer({
  id,
  onClose,
  onSaved,
}: {
  id: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const isNew = id === null;
  const [form, setForm] = useState<FormState>(empty);
  const [busy, setBusy] = useState(false);

  const customersQ = useQuery({ queryKey: ['cargo', 'customers'], queryFn: cargoApi.customers });
  const warehousesQ = useQuery({ queryKey: ['cargo', 'warehouses'], queryFn: cargoApi.warehouses });
  const detailQ = useQuery({
    queryKey: ['cargo', 'shipment', id],
    queryFn: () => cargoApi.shipment(id!),
    enabled: !isNew,
  });
  const nextNoQ = useQuery({
    queryKey: ['cargo', 'next-tracking'],
    queryFn: cargoApi.nextTrackingNo,
    enabled: isNew,
  });

  const detail = detailQ.data;

  useEffect(() => {
    if (detail) {
      setForm({
        FKCargoCustomerId: detail.FKCargoCustomerId ?? '',
        WarehouseCode: detail.WarehouseCode ?? '',
        FKOriginWarehouseId: detail.FKOriginWarehouseId ?? '',
        TransportMode: detail.TransportMode,
        WeightKg: detail.WeightKg,
        VolumeM3: detail.VolumeM3,
        Length: detail.Length,
        Width: detail.Width,
        Height: detail.Height,
        PieceCount: detail.PieceCount,
        DeclaredContent: detail.DeclaredContent ?? '',
        DeliveryMethod: detail.DeliveryMethod,
        AutoCharge: false,
        Charge: detail.Charge,
        Note: detail.Note ?? '',
      });
    }
  }, [detail]);

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    if (!form.FKCargoCustomerId) return toast('Харилцагч сонгоно уу.', 'warning');
    setBusy(true);
    try {
      await cargoApi.saveShipment(id, {
        FKCargoCustomerId: form.FKCargoCustomerId,
        WarehouseCode: form.WarehouseCode || null,
        FKOriginWarehouseId: form.FKOriginWarehouseId || null,
        TransportMode: form.TransportMode,
        WeightKg: form.WeightKg,
        VolumeM3: form.VolumeM3,
        Length: form.Length,
        Width: form.Width,
        Height: form.Height,
        PieceCount: form.PieceCount,
        DeclaredContent: form.DeclaredContent || null,
        DeliveryMethod: form.DeliveryMethod,
        AutoCharge: form.AutoCharge,
        Charge: form.Charge,
        Note: form.Note || null,
      });
      toast('Хадгаллаа.', 'success');
      onSaved();
    } catch (e) {
      toast((e as Error).message, 'danger');
    } finally {
      setBusy(false);
    }
  }

  async function changeStatus(status: number) {
    if (!id) return;
    setBusy(true);
    try {
      await cargoApi.changeStatus(id, { Status: status, WarehouseId: form.FKOriginWarehouseId || null });
      toast('Төлөв шинэчлэгдлээ.', 'success');
      detailQ.refetch();
      onSaved();
    } catch (e) {
      toast((e as Error).message, 'danger');
    } finally {
      setBusy(false);
    }
  }

  const title = isNew ? 'Шинэ илгээмж' : detail?.TrackingNo ?? 'Илгээмж';

  return (
    <Drawer
      title={title}
      open
      onClose={onClose}
      footer={
        <>
          <button className="ts-btn ts-btn-ghost" onClick={onClose}>Болих</button>
          <button className="ts-btn ts-btn-primary" disabled={busy} onClick={save}>
            <i className="bi bi-check2" /> Хадгалах
          </button>
        </>
      }
    >
      {!isNew && detail && (
        <div className="ts-flex-row" style={{ gap: 10 }}>
          <StatusBadge label={detail.StatusLabel} color={SHIPMENT_STATUS_COLOR[detail.Status]} />
          <span className="ts-page-sub">Төлбөр: {fmt(detail.Charge)} · {detail.PaymentStatusLabel}</span>
        </div>
      )}

      <SectionCard icon="bi-info-circle" title="Үндсэн мэдээлэл" hint={isNew ? `Track №: ${nextNoQ.data?.TrackingNo ?? '...'}` : undefined}>
        <div className="ts-row">
          <div style={{ gridColumn: 'span 6' }}>
            <Field label="Харилцагч" required>
              <select className="ts-select" required value={form.FKCargoCustomerId} onChange={(e) => {
                const c = customersQ.data?.find((x) => x.Id === e.target.value);
                set('FKCargoCustomerId', e.target.value);
                if (c) set('WarehouseCode', c.WarehouseCode);
              }}>
                <option value="">— сонгох —</option>
                {customersQ.data?.map((c) => (
                  <option key={c.Id} value={c.Id}>{c.Name} ({c.WarehouseCode})</option>
                ))}
              </select>
            </Field>
          </div>
          <div style={{ gridColumn: 'span 3' }}>
            <Field label="Агуулахын код"><input className="ts-input" value={form.WarehouseCode} onChange={(e) => set('WarehouseCode', e.target.value)} /></Field>
          </div>
          <div style={{ gridColumn: 'span 3' }}>
            <Field label="Тээврийн төрөл">
              <select className="ts-select" value={form.TransportMode} onChange={(e) => set('TransportMode', Number(e.target.value))}>
                {toOptions(TRANSPORT_MODE).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ gridColumn: 'span 6' }}>
            <Field label="Гарах агуулах">
              <select className="ts-select" value={form.FKOriginWarehouseId} onChange={(e) => set('FKOriginWarehouseId', e.target.value)}>
                <option value="">— сонгох —</option>
                {warehousesQ.data?.map((w) => <option key={w.Id} value={w.Id}>{w.Name}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ gridColumn: 'span 6' }}>
            <Field label="Хүргэлтийн арга">
              <select className="ts-select" value={form.DeliveryMethod} onChange={(e) => set('DeliveryMethod', Number(e.target.value))}>
                {toOptions(DELIVERY_METHOD).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ gridColumn: 'span 12' }}>
            <Field label="Агуулга"><input className="ts-input" value={form.DeclaredContent} onChange={(e) => set('DeclaredContent', e.target.value)} placeholder="Жишээ: хувцас, гутал" /></Field>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon="bi-rulers" title="Хэмжээ & төлбөр">
        <div className="ts-row">
          <div style={{ gridColumn: 'span 3' }}><Field label="Жин (кг)"><input className="ts-input" type="number" step="0.01" value={form.WeightKg} onChange={(e) => set('WeightKg', Number(e.target.value))} /></Field></div>
          <div style={{ gridColumn: 'span 3' }}><Field label="Эзлэхүүн (m³)"><input className="ts-input" type="number" step="0.001" value={form.VolumeM3} onChange={(e) => set('VolumeM3', Number(e.target.value))} /></Field></div>
          <div style={{ gridColumn: 'span 2' }}><Field label="Ширхэг"><input className="ts-input" type="number" value={form.PieceCount} onChange={(e) => set('PieceCount', Number(e.target.value))} /></Field></div>
          <div style={{ gridColumn: 'span 4' }}>
            <Field label="Хэмжээ (У×Ө×Ө см)">
              <div className="ts-flex-row">
                <input className="ts-input" type="number" value={form.Length} onChange={(e) => set('Length', Number(e.target.value))} />
                <input className="ts-input" type="number" value={form.Width} onChange={(e) => set('Width', Number(e.target.value))} />
                <input className="ts-input" type="number" value={form.Height} onChange={(e) => set('Height', Number(e.target.value))} />
              </div>
            </Field>
          </div>
          <div style={{ gridColumn: 'span 4' }}>
            <Field label="Төлбөр (₮)" hint={form.AutoCharge ? 'Тарифаар автомат тооцно' : undefined}>
              <input className="ts-input" type="number" value={form.Charge} readOnly={form.AutoCharge} onChange={(e) => set('Charge', Number(e.target.value))} />
            </Field>
          </div>
          <div style={{ gridColumn: 'span 8', display: 'flex', alignItems: 'flex-end' }}>
            <label className="ts-flex-row" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={form.AutoCharge} onChange={(e) => set('AutoCharge', e.target.checked)} />
              <span>Тарифаар автомат тооцоолох</span>
            </label>
          </div>
          <div style={{ gridColumn: 'span 12' }}><Field label="Тэмдэглэл"><input className="ts-input" value={form.Note} onChange={(e) => set('Note', e.target.value)} /></Field></div>
        </div>
      </SectionCard>

      {!isNew && detail && (
        <>
          <SectionCard icon="bi-arrow-repeat" title="Төлөв шилжүүлэх">
            <div className="ts-flex-row" style={{ flexWrap: 'wrap', gap: 8 }}>
              {SCAN_ACTIONS.map((a) => (
                <button key={a.status} className="ts-btn" disabled={busy} onClick={() => changeStatus(a.status)}>
                  <i className={`bi ${a.icon}`} /> {a.label}
                </button>
              ))}
            </div>
          </SectionCard>

          <SectionCard icon="bi-clock-history" title="Хөдөлгөөний түүх">
            <div className="cg-timeline">
              {detail.TrackingEvents.length === 0 && <div className="ts-page-sub">Бичлэг алга.</div>}
              {detail.TrackingEvents.map((e) => (
                <div key={e.Id} className="cg-tl-item">
                  <div className="cg-tl-dot" />
                  <div>
                    <div style={{ fontWeight: 600 }}>{e.EventTypeLabel}</div>
                    <div className="ts-page-sub">
                      {dt(e.OccurredOn)} {e.WarehouseName ? `· ${e.WarehouseName}` : ''} {e.Note ? `· ${e.Note}` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </>
      )}
    </Drawer>
  );
}
